import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { useStartAIImportHook } from "@/hooks";
import { useAiImportRuntime } from "@/store/aiImportRuntime";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { optionalFiles, optionalFilesXlsxOnly, requiredFiles } from "@/utils/form";
import { FileUploadComponent } from "../../form";
import { useAIImportStatusPolling } from "@/hooks/queries";
import { processDocuments } from "@/pages/CreateTender/Shared/helpers";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { TFunction } from "i18next";
import { SUPPORTED_FILE_TYPES_STRING } from "@/constants/documents";
import { CircleLoader } from "../../loaders";
import { InformationCircleIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

export const createDocumentSchema = (t: TFunction) => {
  return z.object({
    publication: requiredFiles(),
    serviceDescription: requiredFiles(),
    contract: optionalFiles(),
    otherDocuments: optionalFiles(),
    priceForms: optionalFilesXlsxOnly(),
  }).check((ctx) => {
    const totalFiles = [
      ...(ctx.value.publication || []),
      ...(ctx.value.serviceDescription || []),
      ...(ctx.value.contract || []),
      ...(ctx.value.otherDocuments || []),
      ...(ctx.value.priceForms || [])
    ].length;

    if (totalFiles > 10) {
      ctx.issues.push({
        code: "custom",
        message: t("schemas:createtender.document.maxfileserror") + " " + t("schemas:createtender.document.aiimporterrornote"),
        path: ["maxerror"],
        input: totalFiles,
      });
    }
  });
};

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
};

function AIImportModal({ open, onOpenChange }: Props) {

  // Hooks
  const { t } = useTranslation(["translation", "modals", "common"]);
  const { startAIImport, loading } = useStartAIImportHook();
  const { setIsPolling } = useAIImportStatusPolling();
  const beginImport = useAiImportRuntime((s) => s.beginImport);

  const formFields = [
    {
      name: "publication",
      label: t("modals:aiimport.inputs.publication.label"),
      article: t("modals:aiimport.inputs.publication.article"),
      description: t("modals:aiimport.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
    },
    {
      name: "serviceDescription",
      label: t("modals:aiimport.inputs.servicedescription.label"),
      article: t("modals:aiimport.inputs.servicedescription.article"),
      description: t("modals:aiimport.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
    },
    {
      name: "contract",
      label: t("modals:aiimport.inputs.contract.label"),
      article: t("modals:aiimport.inputs.contract.article"),
      description: t("modals:aiimport.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
      showOptional: true,
    },
    {
      name: "priceForms",
      label: t("modals:aiimport.inputs.priceform.label"),
      article: t("modals:aiimport.inputs.priceform.article"),
      description: t("modals:aiimport.inputdescription", { fileTypes: "XLSX" }),
      showOptional: true,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      },
    },
    {
      name: "otherDocuments",
      label: t("modals:aiimport.inputs.other.label"),
      article: t("modals:aiimport.inputs.other.article"),
      description: t("modals:aiimport.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
      showOptional: true,
    },
  ];

  const FormSchema = createDocumentSchema(t);

  const onInvalid = (errors: unknown) => console.error(errors);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    mode: "onChange"
  });

  const { control } = form;

  // Watch all file upload fields efficiently
  const [publication, serviceDescription, contract, otherDocuments, priceForms] = useWatch({
    control,
    name: ["publication", "serviceDescription", "contract", "otherDocuments", "priceForms"],
  });

  // Calculate total files across all fields
  const totalFiles =
    (publication?.length ?? 0) +
    (serviceDescription?.length ?? 0) +
    (contract?.length ?? 0) +
    (otherDocuments?.length ?? 0) +
    (priceForms?.length ?? 0);

  const maxFilesExceeded = totalFiles > 10;
  const maxFilesError = form.formState.errors["maxerror"];

  const onSubmit = (data: z.infer<typeof FormSchema>) => {
    const { documents, categoryIndexes } = processDocuments(
      data as Record<string, File[]>,
      {
        publication: "publications",
        serviceDescription: "serviceDescription",
        contract: "contracts",
        otherDocuments: "otherDocuments",
        priceForms: "priceForms"
      }
    );

    const documentsArray = documents;
    const variables: Record<string, any> = {
      documents: documentsArray,
      publications: categoryIndexes.publications || [],
      serviceDescription: categoryIndexes.serviceDescription || [],
      contracts: categoryIndexes.contracts || [],
      otherDocuments: categoryIndexes.otherDocuments || [],
      priceForms: categoryIndexes.priceForms || []
    };

    startAIImport({
      variables,
      onCompleted: (data) => {
        // Ensure polling refreshes server-side statuses
        setIsPolling(true);

        // Immediately trigger SSE runtime and show loader for returned AI import id
        const aiImportId = data?.startAIImport?.id;
        if (typeof aiImportId === "number") {
          beginImport(aiImportId, false); // Explicitly mark as NOT resuming
        }
      },
    }).then(() => {
      // Reset the form
      form.reset();
      // Close the modal
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[60vw] max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <Form {...form}>
          <form className="space-y-6" id="ai-import-form" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <DialogHeader>
              <DialogTitle className="flex items-center text-blue-500 mb-2"><SparklesIcon className="h-4 w-4 mr-2" />
                {t("modals:aiimport.title")}
              </DialogTitle>
              <DialogDescription>
                {t("modals:aiimport.description")}
              </DialogDescription>
            </DialogHeader>

            {/* File limit information */}
            {(maxFilesError || maxFilesExceeded) && (
              <Card className="border-blue-200 bg-blue-50 p-4">
                <div className="flex gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <h3 className="text-blue-900 font-semibold text-sm">
                      {t("modals:aiimport.filelimitinfo.title")}
                    </h3>
                    <div className="text-blue-800 text-sm space-y-2">
                      <p>
                        {t("modals:aiimport.filelimitinfo.description")}
                      </p>
                      <div className="space-y-1.5 mt-2">
                        <p className="font-medium">{t("modals:aiimport.filelimitinfo.uploadguide")}</p>
                        <ul className="list-disc list-inside ml-2 space-y-1">
                          <li>{t("modals:aiimport.filelimitinfo.documenttypes.publications")}</li>
                          <li>{t("modals:aiimport.filelimitinfo.documenttypes.servicedescriptions")}</li>
                          <li>{t("modals:aiimport.filelimitinfo.documenttypes.contracts")}</li>
                          <li>{t("modals:aiimport.filelimitinfo.documenttypes.evaluationcriteria")}</li>
                        </ul>
                      </div>
                      <div className="flex items-start gap-2 mt-3 p-3 bg-blue-100 rounded-md">
                        <DocumentTextIcon className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                        <p>
                          <strong>{t("modals:aiimport.filelimitinfo.notetitle")}</strong> {t("modals:aiimport.filelimitinfo.notedescription")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex-col-4">
              {
                formFields.map((field, index) => (
                  <FileUploadComponent
                    key={index}
                    name={field.name}
                    label={field.label}
                    article={field.article}
                    description={field.description}
                    form={form}
                    showOptional={field.showOptional}
                    accept={field.accept}
                  />
                ))
              }
              {maxFilesError && (
                <Card className="bg-red-50 border-red-500 border text-red-500 p-2 rounded-md">
                  <p className="text-sm">{maxFilesError.message}</p>
                </Card>
              )}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant={"outline"}>
                  {t("common:buttons.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" className={cn("gap-2", loading && "cursor-progress")} disabled={loading}>
                <CircleLoader
                  visible={loading}
                  height={18}
                  width={18}
                  strokeWidth={2}
                />
                {loading ? (
                  t("common:buttons.submitting")
                ) : (
                  t("common:buttons.submit")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AIImportModal;
